import { useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Briefcase, Store, Users, Gift, ArrowRight,
  Mail, Lock, Eye, EyeOff, User, Phone,
  Building2, CheckCircle2,
  TrendingUp, Zap, PieChart,
  MapPin, Percent, Network,
  Wallet, History, ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useAuth, getRoleRedirect } from "@/contexts/AuthContext";
import { getPortalForRole, getPortalUrl, PORTAL_LABELS, type PortalId } from "@/lib/portal";
import { useToast } from "@/hooks/use-toast";
import { useBranding } from "@/contexts/BrandingContext";
import { usePortal } from "@/contexts/PortalContext";

// ─── Role config (used for the main-domain role picker) ────────────────────
const roles = [
  {
    key: "corporate",
    icon: Briefcase,
    title: "Corporate / Issuer",
    desc: "Issue and manage digital vouchers for employees, customers, or partners.",
    gradient: "from-blue-600 to-indigo-700",
    badge: "For businesses",
    authRole: "corporate_admin",
  },
  {
    key: "merchant",
    icon: Store,
    title: "Merchant",
    desc: "Accept voucher payments at your business and receive instant settlement.",
    gradient: "from-emerald-600 to-teal-700",
    badge: "For retailers",
    authRole: "merchant_admin",
  },
  {
    key: "agent",
    icon: Users,
    title: "Agent",
    desc: "Sell vouchers in your territory and earn commission on every sale.",
    gradient: "from-purple-600 to-violet-700",
    badge: "For resellers",
    authRole: "agent",
  },
  {
    key: "consumer",
    icon: Gift,
    title: "Consumer",
    desc: "Create a wallet to store, manage, and redeem your gift vouchers.",
    gradient: "from-amber-500 to-orange-600",
    badge: "For individuals",
    authRole: "consumer",
  },
];

const PORTAL_TO_ROLE_KEY: Record<string, string> = {
  consumer: "consumer",
  merchant: "merchant",
  corporate: "corporate",
  agent: "agent",
};

const ROLE_TO_PORTAL_REGISTER: Record<string, string> = {
  consumer:  "https://app.valuegiftvouchers.com/register",
  merchant:  "https://merchant.valuegiftvouchers.com/register",
  corporate: "https://corporate.valuegiftvouchers.com/register",
  agent:     "https://agent.valuegiftvouchers.com/register",
};

// ─── Portal left-panel config ───────────────────────────────────────────────
type FeatureItem = { icon: React.ElementType; text: string };
type PanelConfig = {
  gradient: string;
  badge: string;
  heading: string;
  sub: string;
  features: FeatureItem[];
  btnClass: string;
  loginHref: string;
  extraField?: "company" | "business";
};

const PORTAL_PANEL: Record<string, PanelConfig> = {
  corporate: {
    gradient: "from-blue-700 via-indigo-800 to-blue-950",
    badge: "For Businesses",
    heading: "Issue Vouchers at Scale",
    sub: "Empower employees, reward customers, and run promotions — all from one dashboard.",
    features: [
      { icon: Building2,  text: "Bulk-issue vouchers to teams in seconds" },
      { icon: TrendingUp, text: "Live spend analytics & budget controls" },
      { icon: ShieldCheck,text: "Audit-ready records, BoG compliant" },
      { icon: CheckCircle2, text: "Custom face values, expiry & branding" },
    ],
    btnClass: "bg-blue-600 hover:bg-blue-700 text-white",
    loginHref: "https://corporate.valuegiftvouchers.com/login",
    extraField: "company",
  },
  merchant: {
    gradient: "from-emerald-600 via-teal-700 to-green-900",
    badge: "For Retailers",
    heading: "Accept Vouchers. Get Paid Fast.",
    sub: "Join thousands of merchants accepting digital vouchers with instant MoMo or bank settlement.",
    features: [
      { icon: Zap,         text: "Scan & verify any VGV voucher in seconds" },
      { icon: TrendingUp,  text: "Same-day MoMo or bank settlement" },
      { icon: PieChart,    text: "Real-time redemption dashboard & reports" },
      { icon: CheckCircle2,text: "Free to join — zero setup fees" },
    ],
    btnClass: "bg-emerald-600 hover:bg-emerald-700 text-white",
    loginHref: "https://merchant.valuegiftvouchers.com/login",
    extraField: "business",
  },
  agent: {
    gradient: "from-purple-700 via-violet-800 to-purple-950",
    badge: "For Resellers",
    heading: "Sell. Earn. Grow.",
    sub: "Build your voucher distribution business and earn a commission on every single sale.",
    features: [
      { icon: Percent,     text: "Earn commission on every voucher sold" },
      { icon: MapPin,      text: "Manage your territory & client merchants" },
      { icon: Network,     text: "Real-time earnings & downline dashboard" },
      { icon: CheckCircle2,text: "Flexible payout — MoMo or bank transfer" },
    ],
    btnClass: "bg-purple-600 hover:bg-purple-700 text-white",
    loginHref: "https://agent.valuegiftvouchers.com/login",
  },
  consumer: {
    gradient: "from-amber-500 via-orange-500 to-rose-600",
    badge: "For Individuals",
    heading: "Your Digital Voucher Wallet",
    sub: "Store, send, and redeem gift vouchers from your favourite brands — all in one safe place.",
    features: [
      { icon: Gift,        text: "Receive vouchers from any issuer or brand" },
      { icon: Wallet,      text: "Redeem at 2,000+ merchant locations" },
      { icon: History,     text: "Full balance history & transaction log" },
      { icon: ShieldCheck, text: "Secure & Bank of Ghana compliant" },
    ],
    btnClass: "bg-amber-500 hover:bg-amber-600 text-white",
    loginHref: "https://app.valuegiftvouchers.com/login",
  },
};

// ─── Decorative blobs per portal ────────────────────────────────────────────
const PORTAL_BLOBS: Record<string, string> = {
  corporate: "from-blue-400/20",
  merchant:  "from-emerald-400/20",
  agent:     "from-purple-400/20",
  consumer:  "from-amber-400/20",
};

// ─── Left panel component ────────────────────────────────────────────────────
const PortalLeftPanel = ({
  portalKey,
  iconLogo,
}: {
  portalKey: string;
  iconLogo: string;
}) => {
  const cfg = PORTAL_PANEL[portalKey];
  const blob = PORTAL_BLOBS[portalKey] ?? "from-white/10";
  if (!cfg) return null;

  return (
    <div
      className={`hidden lg:flex flex-col justify-between relative overflow-hidden bg-gradient-to-br ${cfg.gradient} p-10 text-white`}
    >
      {/* Decorative blobs */}
      <div className={`absolute -top-24 -right-24 w-80 h-80 rounded-full bg-gradient-radial ${blob} to-transparent blur-3xl pointer-events-none`} />
      <div className={`absolute -bottom-32 -left-16 w-96 h-96 rounded-full bg-gradient-radial ${blob} to-transparent blur-3xl pointer-events-none`} />

      {/* Logo + badge */}
      <div className="relative z-10">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center ring-1 ring-white/20">
            <img src={iconLogo} alt="VGV" className="w-8 h-8 object-contain" />
          </div>
          <span className="text-xs font-semibold tracking-widest uppercase text-white/70">
            {cfg.badge}
          </span>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl xl:text-4xl font-bold leading-tight mb-4 text-white">
            {cfg.heading}
          </h2>
          <p className="text-white/70 text-base leading-relaxed mb-10 max-w-xs">
            {cfg.sub}
          </p>

          <ul className="space-y-4">
            {cfg.features.map((f, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + i * 0.1, duration: 0.5 }}
                className="flex items-start gap-3"
              >
                <div className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-full bg-white/15 flex items-center justify-center">
                  <f.icon className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-sm text-white/85 leading-snug">{f.text}</span>
              </motion.li>
            ))}
          </ul>
        </motion.div>
      </div>

      {/* Bottom trust badge */}
      <div className="relative z-10">
        <div className="flex items-center gap-2 text-xs text-white/50">
          <ShieldCheck className="w-4 h-4" />
          <span>Bank of Ghana Compliant · Secure · Multi-Merchant</span>
        </div>
      </div>
    </div>
  );
};

// ─── Main RegisterPage ───────────────────────────────────────────────────────
const RegisterPage = () => {
  const { role } = useParams();
  const navigate = useNavigate();
  const { signUp, user, profile, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const { fullLogo, iconLogo } = useBranding();
  const { portal, isPortalMode } = usePortal();

  // All useState calls must come before any early returns (Rules of Hooks)
  const [fullName, setFullName]           = useState("");
  const [companyName, setCompanyName]     = useState("");
  const [email, setEmail]                 = useState("");
  const [phone, setPhone]                 = useState("");
  const [password, setPassword]           = useState("");
  const [showPassword, setShowPassword]   = useState(false);
  const [loading, setLoading]             = useState(false);

  // Admin portal has no self-registration
  if (portal === "admin") return <Navigate to="/login" replace />;

  if (authLoading) {
    return <div className="h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }
  // Already logged in — redirect to the user's portal (full URL, not relative path)
  if (user) {
    if (profile) {
      const portalId = getPortalForRole(profile.role);
      if (portalId) {
        window.location.href = getPortalUrl(portalId, getRoleRedirect(profile.role));
        return null;
      }
    }
    return <Navigate to="/" replace />;
  }

  const portalRoleKey = portal ? PORTAL_TO_ROLE_KEY[portal] : undefined;
  const activeRoleKey = role || portalRoleKey;

  // Main domain + role param → redirect to portal
  if (!isPortalMode && role) {
    const dest = ROLE_TO_PORTAL_REGISTER[role];
    if (dest) { window.location.replace(dest); return null; }
  }

  // ── Role picker (main domain /register with no role selected) ──────────────
  if (!activeRoleKey) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/40 flex flex-col">
        {/* Top bar */}
        <div className="w-full flex items-center justify-between px-8 py-5">
          <a href="https://valuegiftvouchers.com">
            <img src={iconLogo} alt="VGV" className="h-12 w-12 object-contain" />
          </a>
          <p className="text-sm text-muted-foreground">
            Already have an account?{" "}
            <a href="https://valuegiftvouchers.com/login"
               className="text-brand-green-dk hover:text-brand-forest font-semibold transition-colors">
              Sign In
            </a>
          </p>
        </div>

        {/* Hero */}
        <div className="text-center px-6 pt-8 pb-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-brand-green bg-brand-green-lt px-3 py-1 rounded-full mb-4">
              Ghana's Digital Voucher Platform
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-4 leading-tight">
              Create Your <span className="text-brand-green">Account</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-lg mx-auto">
              Select the account type that best describes how you'll use the platform.
            </p>
          </motion.div>
        </div>

        {/* Role cards */}
        <div className="flex-1 flex items-start justify-center px-6 pb-16">
          <div className="w-full max-w-4xl">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {roles.map((r, i) => {
                const cardHref = !isPortalMode ? ROLE_TO_PORTAL_REGISTER[r.key] : `/register/${r.key}`;
                return (
                  <motion.div key={r.key} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1, duration: 0.5 }} className="group">
                    <a href={cardHref} className="block h-full">
                      <div className="h-full bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden group-hover:-translate-y-1.5">
                        <div className={`bg-gradient-to-br ${r.gradient} p-7 flex flex-col items-center justify-center gap-3`}>
                          <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                            <r.icon className="w-7 h-7 text-white" />
                          </div>
                          <span className="text-xs font-semibold text-white/80 tracking-wide uppercase">{r.badge}</span>
                        </div>
                        <div className="p-5">
                          <h3 className="text-base font-bold text-foreground mb-2">{r.title}</h3>
                          <p className="text-sm text-muted-foreground mb-5 leading-relaxed">{r.desc}</p>
                          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-green group-hover:gap-3 transition-all duration-200">
                            Get Started <ArrowRight className="w-4 h-4" />
                          </span>
                        </div>
                      </div>
                    </a>
                  </motion.div>
                );
              })}
            </div>
            <p className="text-center text-xs text-muted-foreground mt-10">
              By creating an account you agree to our{" "}
              <a href="https://valuegiftvouchers.com#terms" className="underline hover:text-brand-forest transition-colors">Terms of Service</a>
              {" "}and{" "}
              <a href="https://valuegiftvouchers.com#privacy" className="underline hover:text-brand-forest transition-colors">Privacy Policy</a>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Portal registration form ───────────────────────────────────────────────
  const selectedRole = roles.find((r) => r.key === activeRoleKey);
  if (!selectedRole) {
    return <div className="min-h-screen flex items-center justify-center">Invalid role</div>;
  }

  const panelCfg = PORTAL_PANEL[activeRoleKey];
  const loginHref = panelCfg?.loginHref ?? "/login";
  const showCompanyField = panelCfg?.extraField === "company";
  const showBusinessField = panelCfg?.extraField === "business";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 10) {
      toast({ title: "Password must be at least 10 characters", variant: "destructive" });
      return;
    }
    setLoading(true);

    // Pre-flight: check if email already belongs to an account on any portal.
    // Uses a SECURITY DEFINER RPC so the anon role can read profiles safely.
    const { data: existingPortal } = await supabase.rpc("get_portal_for_email", {
      p_email: email.trim().toLowerCase(),
    });

    if (existingPortal) {
      const portalLabel = PORTAL_LABELS[existingPortal as PortalId] ?? existingPortal;
      const loginUrl = getPortalUrl(existingPortal as PortalId, "/login");
      setLoading(false);
      toast({
        title: "Account already exists",
        description: `An account with this email is already registered on the ${portalLabel} portal. Redirecting you to sign in…`,
        variant: "destructive",
      });
      setTimeout(() => { window.location.href = loginUrl; }, 2500);
      return;
    }

    const { error } = await signUp(email, password, {
      role: selectedRole.authRole,
      full_name: fullName,
      phone: phone ? `+233${phone}` : "",
      ...(companyName ? { company_name: companyName } : {}),
    });

    setLoading(false);

    if (error) {
      const isAlreadyRegistered =
        error.message?.toLowerCase().includes("already registered") ||
        error.message?.toLowerCase().includes("already exists") ||
        error.message?.toLowerCase().includes("user_already_exists");

      if (isAlreadyRegistered) {
        toast({
          title: "Account already exists",
          description: "An account with this email already exists. Please sign in instead.",
          variant: "destructive",
        });
      } else {
        toast({ title: "Registration failed", description: error.message, variant: "destructive" });
      }
    } else {
      navigate("/verify-email");
    }
  };

  // Email placeholder per portal
  const emailPlaceholder: Record<string, string> = {
    corporate: "contact@yourcompany.com",
    merchant:  "manager@yourbusiness.com",
    agent:     "you@example.com",
    consumer:  "you@gmail.com",
  };

  // Full-name label per portal
  const nameLabel: Record<string, string> = {
    corporate: "Contact Person Name",
    merchant:  "Owner / Manager Name",
    agent:     "Full Name",
    consumer:  "Full Name",
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[420px_1fr] xl:grid-cols-[480px_1fr]">

      {/* ── Left panel ── */}
      <PortalLeftPanel portalKey={activeRoleKey} iconLogo={iconLogo} />

      {/* ── Right panel (form) ── */}
      <div className="flex flex-col justify-center items-center p-6 sm:p-10 bg-slate-50/60">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <div className="mb-8 text-center">
            <a href="https://valuegiftvouchers.com">
              <img src={fullLogo} alt="Value Gift Vouchers" className="h-64 mx-auto mb-6 object-contain" />
            </a>
            <h1 className="text-2xl font-bold text-foreground mb-1">Create your account</h1>
            <p className="text-sm text-muted-foreground">
              {selectedRole.title} registration · Get started for free
            </p>
          </div>

          {/* Form card */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
            <form onSubmit={handleSubmit} className="space-y-5">

              {/* Company Name (corporate only) */}
              {showCompanyField && (
                <div className="space-y-1.5">
                  <Label htmlFor="companyName" className="text-sm font-medium">Company Name</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="companyName"
                      type="text"
                      placeholder="Acme Ghana Ltd."
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      className="pl-10 h-11"
                      required
                    />
                  </div>
                </div>
              )}

              {/* Business Name (merchant only) */}
              {showBusinessField && (
                <div className="space-y-1.5">
                  <Label htmlFor="companyName" className="text-sm font-medium">Business Name</Label>
                  <div className="relative">
                    <Store className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="companyName"
                      type="text"
                      placeholder="My Supermarket Ltd."
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      className="pl-10 h-11"
                      required
                    />
                  </div>
                </div>
              )}

              {/* Full Name */}
              <div className="space-y-1.5">
                <Label htmlFor="fullName" className="text-sm font-medium">
                  {nameLabel[activeRoleKey] ?? "Full Name"}
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="fullName"
                    type="text"
                    placeholder="Kwame Asante"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="pl-10 h-11"
                    required
                  />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-sm font-medium">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder={emailPlaceholder[activeRoleKey] ?? "you@example.com"}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-11"
                    required
                  />
                </div>
              </div>

              {/* Phone */}
              <div className="space-y-1.5">
                <Label htmlFor="phone" className="text-sm font-medium">
                  Phone Number <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <span className="absolute left-10 top-1/2 -translate-y-1/2 text-sm text-muted-foreground select-none">+233</span>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="24 123 4567"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 9))}
                    className="pl-[4.5rem] h-11"
                    maxLength={9}
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Min. 10 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 h-11"
                    required
                    minLength={10}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={loading}
                className={`w-full h-12 text-base font-semibold shadow-sm ${panelCfg?.btnClass ?? "bg-brand-forest hover:bg-brand-green-dk text-white"}`}
              >
                {loading ? "Creating account…" : "Create Account"}
              </Button>
            </form>

            {/* ToS note */}
            <p className="mt-4 text-center text-[11px] text-muted-foreground leading-relaxed">
              By creating an account you agree to our{" "}
              <a href="https://valuegiftvouchers.com#terms" className="underline hover:text-brand-forest transition-colors">Terms of Service</a>
              {" "}and{" "}
              <a href="https://valuegiftvouchers.com#privacy" className="underline hover:text-brand-forest transition-colors">Privacy Policy</a>.
            </p>
          </div>

          {/* Footer links */}
          <div className="mt-6 flex items-center justify-between text-sm">
            <a href="https://valuegiftvouchers.com" className="text-muted-foreground hover:text-foreground transition-colors">
              ← Back to main site
            </a>
            <a href={loginHref} className="text-brand-green-dk hover:text-brand-forest font-semibold transition-colors">
              Already have an account?
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default RegisterPage;
